/**
 * The arithmetic of the quantity dialog, apart from the page so it can be
 * run by itself (see howmany_test.go and invert_test.go). Asked for on
 * 2026-09-27: "The sell and buy dialogs should allow selecting useful
 * quantities, like 1, a stack, half of all possible and all."
 */

/**
 * Where one of the dialog's quick buttons puts the slider, in lots: one,
 * the other side of where it is now, half of all there is to trade, or all
 * of it. Always somewhere on the slider, so half of one lot is one.
 *
 * The second button was "A stack", a square's worth of the thing, until
 * 2026-10-04: "Remove item stacking and reintroduce encumbrance. Replace
 * the stack count helper button with invert". With no squares to count by
 * there is no stack to set the slider to, and Invert turns what it says
 * into the rest of what there is: the most less what it says now, and never
 * less than one, which is the slider's floor. Ten of sixty picked becomes
 * fifty — all but those ten — and a second press puts it back.
 */
export function presetLots(which: string, most: number, now: number): number {
  const n = which === "one" ? 1 : which === "invert" ? most - now : which === "half" ? Math.floor(most / 2) : most;
  return Math.max(1, Math.min(most, n));
}

/**
 * How the slider and the quick buttons are set for a trade of `most` lots
 * at most. Asked for on 2026-10-08: "when there's only 1 of an item, the
 * slider should show 1 as being the full value, and invert and half should
 * be greyed out".
 *
 * A slider whose floor and top are both one draws its thumb at the empty
 * end, which reads as none of it. With one lot the slider runs from nought
 * instead, so its one is the top and the track is drawn full; the dialog
 * never takes nought from it (paintHowMany keeps the count at one or more,
 * and puts the thumb straight back), so the number still says one lot.
 * With more it runs from one, as it always has.
 *
 * Invert and Half are off whenever there is less than two lots: both can
 * only land on the one lot the slider already says. One and All stay on,
 * the two ends of the slider even where they are the same place.
 */
export function howManyLayout(most: number): { min: number; max: number; off: string[] } {
  const single = most < 2;
  return { min: single ? 0 : 1, max: Math.max(1, most), off: single ? ["invert", "half"] : [] };
}

/** The gold coin's id: ItemGold, the first id in the server's item table. */
const GOLD = 1;

/** A pile in the bag, as the state sends it. */
export interface Pile {
  id: number;
  count: number;
  owner?: string;
}

/**
 * The most lots of a line of the vendor's stock that can be bought: as many
 * as the purse pays for and the bag can take, whichever is fewer, and never
 * less than one, so a lot out of reach still gets the server's own word on
 * why. The bag has room for any number of kinds and any amount of each
 * (2026-10-05: "don't limit the number of different kinds of objects in the
 * inventory"), so what stops it is the weight — every lot adds what its goods weigh and takes off what the coins
 * paid for it weighed, a coin being the unit (2026-10-04: "reintroduce
 * encumbrance").
 */
export function buyableLots(
  inv: Pile[],
  gold: number,
  line: { id: number; count: number; price: number },
  cnOf: (id: number) => number,
  carrying: { cn: number; maxcn: number },
): number {
  const afford = Math.floor(gold / Math.max(1, line.price));
  // What a lot adds to the bag, net of the coins it costs.
  const gain = cnOf(line.id) * line.count - line.price * cnOf(GOLD);
  let lift = afford;
  if (gain > 0) lift = Math.floor((carrying.maxcn - carrying.cn) / gain);
  return Math.max(1, Math.min(afford, lift));
}
